import imageMiniGeneratorNl from '/images/year-overview/mini-opwekkers-nl.png'
import imageMiniGeneratorFr from '/images/year-overview/mini-opwekkers-fr.png'
import referralDefaultImg from '/images/referral/referral-default.jpg'
import classNames from 'classnames'
import ComparisonCard from '../components/comparison-card/ComparisonCard.tsx'
import GasTipCard from '../components/gas-tip-card/GasTipCard.tsx'
import { PersonalProps } from './types.ts'
import EnergyOverviewSlide from '../components/energy-overview-slide/EnergyOverviewSlide.tsx'
import { BrandColors } from '../components/energy-overview-slide/types.ts'
import EnergyOverviewCarousel from '../components/energy-overview-slider/EnergyOverviewCarousel.tsx'
import ThanksSlide from '../components/thanks-slide/ThanksSlide.tsx'
import VerticalSlider from '../components/vertical-slider/VerticalSlider.tsx'
import SmallProducerCard from 'components/small-producer-card/SmallProducerCard.tsx'
import { DATE_FORMAT_LONG } from 'constants/constants.ts'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatAmount } from 'utils/format.ts'
import styles from './EnergyOverviewSection.module.scss'
import Link from 'components/link/Link.tsx'
import { getProducerLocation } from 'utils/producer.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { selectProducerId } from 'store/contact/selectors.ts'
import LoadingCard from 'components/loading-card/LoadingCard.tsx'
import { useGetProducerByIdQuery } from 'store/queries/bolt-api/producers'
import BackdropIcon from 'components/backdrop-icon/BackdropIcon.tsx'
import { Button, Card, Modal } from '@boltenergy-be/design-system'
import { selectCurrentAccount, selectCurrentBillingContract, selectIsProducerAccount } from 'store/contact/slice.ts'
import { LANGUAGES } from '@boltenergy-be/shared'

const SectionPersonal = ({ data, communityReferrals }: PersonalProps) => {
  // REDUX STORE
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const account = useStoreSelector(selectCurrentAccount)
  const producerId = selectProducerId(billingContract)

  // Redux Queries
  const { data: producerData, isLoading } = useGetProducerByIdQuery({ producerIdOrSlug: producerId ?? '' }, { skip: !producerId })

  // i18n
  const { t } = useTranslation(['energyOverview', 'common', 'producer'])
  const language = useGetLanguage()
  const lowercaseLanguage = useGetLanguage('lowercase')

  // Local state
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false)
  const [confetti, setConfetti] = useState<boolean>(false)

  // Constants
  const { electricity, gas, referrals } = data

  return (
    <>
      <Card.Title>{t(($) => $.personal.title)}</Card.Title>

      {isLoading ? (
        <LoadingCard />
      ) : (
        <EnergyOverviewCarousel id="personal" triggerConfetti={(value) => setConfetti(value)}>
          {/* CONSUMED ELECTRICITY SLIDE */}
          {electricity?.consumed?.total && producerData?.producer && (
            <EnergyOverviewSlide
              data={formatAmount(electricity.consumed.total, { fractionDigits: 0 })}
              label={t(($) => $.personal.electricityProducer.label)}
              metric="kWh"
              description={t(($) => $.personal.electricityProducer.description, {
                producer: producerData.producer.name[lowercaseLanguage] ?? '',
                from: dayjs(electricity.consumed.from).format(DATE_FORMAT_LONG),
                to: dayjs(electricity.consumed.to).format(DATE_FORMAT_LONG)
              })}
            >
              {producerData.producer.images && (
                <img
                  src={producerData.producer.images.profile?.url || producerData.producer.images.header?.url}
                  alt={t(($) => $.alt, {
                    defaultValue: '',
                    producer: producerData.producer.name[lowercaseLanguage] ?? '',
                    ns: 'producer'
                  })}
                />
              )}
              <SmallProducerCard
                className={styles['small-card']}
                energyType={producerData.producer.energyType}
                location={getProducerLocation(lowercaseLanguage, producerData.producer.location)}
                title={producerData.producer.name[lowercaseLanguage] ?? ''}
              />
            </EnergyOverviewSlide>
          )}

          {/* CONSUMED ELECTRICITY COMPARISON SLIDE */}
          {electricity?.consumed?.total && (
            <EnergyOverviewSlide
              data={formatAmount(electricity.consumed.total, { fractionDigits: 0 })}
              label={t(($) => $.personal.electricityComparison.label)}
              metric="kWh"
              description={t(($) => $.personal.electricityComparison.description, {
                phone: formatAmount(electricity.consumed.amountOfPhoneCharges, { fractionDigits: 0 }),
                tv: formatAmount(electricity.consumed.tvHours, { fractionDigits: 0 })
              })}
            >
              <VerticalSlider>
                <ComparisonCard
                  data={formatAmount(electricity.consumed.microwaveHours, { fractionDigits: 0 })}
                  description={t(($) => $.personal.electricityComparison.microwave)}
                  icon={<BackdropIcon name="microwave" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.consumed.amountOfPhoneCharges, { fractionDigits: 0 })}
                  description={t(($) => $.personal.electricityComparison.phone)}
                  icon={<BackdropIcon name="smartPhone" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.consumed.laundryMachines, { fractionDigits: 0 })}
                  description={t(($) => $.personal.electricityComparison.laundry)}
                  icon={<BackdropIcon name="washingMachine" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.consumed.tvHours, { fractionDigits: 0 })}
                  description={t(($) => $.personal.electricityComparison.tv)}
                  icon={<BackdropIcon name="tv" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.consumed.vacuumHours, { fractionDigits: 0 })}
                  description={t(($) => $.personal.electricityComparison.vacuum)}
                  icon={<BackdropIcon name="vacuum" />}
                />
              </VerticalSlider>
            </EnergyOverviewSlide>
          )}

          {/* CARBON DIOXIDE AVERAGE SLIDE */}
          {electricity?.carbonDioxide?.reduction && (
            <EnergyOverviewSlide
              color={BrandColors.BLUE}
              data={`-${formatAmount(electricity.carbonDioxide.reduction, { fractionDigits: 0 })}`}
              label={t(($) => $.personal.carbonDioxideAverage.label)}
              metric={t(($) => $.personal.carbonDioxideAverage.metric)}
              description={t(($) => $.personal.carbonDioxideAverage.description)}
            >
              <div className={classNames(styles.graph, styles['graph-emissions-personal'])}>
                <data
                  value={100 / 7.5}
                  className={classNames(styles.you, styles['with-bolt-icon'], { [styles.fr]: language === LANGUAGES.FR })}
                />
                <data value={100} className={classNames(styles.average, { [styles.fr]: language === LANGUAGES.FR })} />
              </div>
            </EnergyOverviewSlide>
          )}

          {/* CARBON DIOXIDE COMPARISON SLIDE */}
          {electricity?.carbonDioxide?.reduction && (
            <EnergyOverviewSlide
              color={BrandColors.BLUE}
              data={`-${formatAmount(electricity.carbonDioxide.reduction, { fractionDigits: 0 })}`}
              label={t(($) => $.personal.carbonDioxideComparison.label)}
              metric={t(($) => $.personal.carbonDioxideComparison.metric)}
              description={t(($) => $.personal.carbonDioxideComparison.description)}
            >
              <VerticalSlider>
                <ComparisonCard
                  data={formatAmount(electricity.carbonDioxide.dieselKilometers, { fractionDigits: 0 })}
                  description={t(($) => $.personal.carbonDioxideComparison.diesel)}
                  icon={<BackdropIcon name="car" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.carbonDioxide.trees, { fractionDigits: 0 })}
                  description={t(($) => $.personal.carbonDioxideComparison.trees)}
                  icon={<BackdropIcon name="trees" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.carbonDioxide.veggieDays, { fractionDigits: 0 })}
                  description={t(($) => $.personal.carbonDioxideComparison.veggie)}
                  icon={<BackdropIcon name="saladBowl" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.carbonDioxide.avocados, { fractionDigits: 0 })}
                  description={t(($) => $.personal.carbonDioxideComparison.avocado)}
                  icon={<BackdropIcon name="avocado" />}
                />
                <ComparisonCard
                  data={formatAmount(electricity.carbonDioxide.steaks, { fractionDigits: 0 })}
                  description={t(($) => $.personal.carbonDioxideComparison.steak)}
                  icon={<BackdropIcon name="steak" />}
                />
              </VerticalSlider>
            </EnergyOverviewSlide>
          )}

          {/* GAS SLIDE */}
          {gas?.consumed?.total && (
            <EnergyOverviewSlide
              color={BrandColors.PURPLE}
              data={formatAmount(gas.consumed.total, { fractionDigits: 0 })}
              label={t(($) => $.personal.gas.label)}
              metric="kWh"
              description={t(($) => $.personal.gas.description, {
                from: dayjs(gas.consumed.from).format(DATE_FORMAT_LONG),
                to: dayjs(gas.consumed.to).format(DATE_FORMAT_LONG),
                emission: formatAmount(gas.consumed.total * 0.202, { fractionDigits: 0 })
              })}
            >
              <VerticalSlider duration={30000} reverse>
                <GasTipCard icon="thermostat" tip={t(($) => $.personal.gas.tips.temperature)} orderNumber={1} />
                <GasTipCard icon="shower" tip={t(($) => $.personal.gas.tips.shower)} orderNumber={2} />
                <GasTipCard icon="gas" tip={t(($) => $.personal.gas.tips.heating)} orderNumber={3} />
                <GasTipCard icon="moon" tip={t(($) => $.personal.gas.tips.night)} orderNumber={4} />
                <GasTipCard icon="hills" tip={t(($) => $.personal.gas.tips.away)} orderNumber={5} />
              </VerticalSlider>

              {/* INFO MODAL TRIGGER */}
              <Button
                leadingIcon="info"
                variant="tertiary"
                className={styles['info-button']}
                size="small"
                aria-label="info"
                onClick={() => setInfoModalOpen(true)}
              />

              {/* INFO MODAL */}
              <Modal
                isOpen={infoModalOpen}
                title={parse(t(($) => $.personal.gas.modalText))}
                onRequestClose={() => setInfoModalOpen(false)}
              />
            </EnergyOverviewSlide>
          )}

          {/* MINI GENERATORS SLIDE */}
          {electricity?.produced?.total && (
            <EnergyOverviewSlide
              color={BrandColors.ORANGE}
              data={formatAmount(electricity.produced.total, { fractionDigits: 0 })}
              label={t(($) => $.personal.miniGenerators.label)}
              metric="kWh"
              description={t(($) => $.personal.miniGenerators.description, {
                from: dayjs(electricity.produced.from).format(DATE_FORMAT_LONG),
                to: dayjs(electricity.produced.to).format(DATE_FORMAT_LONG)
              })}
            >
              <figure className={styles['mini-figure']}>
                <img
                  src={language === LANGUAGES.FR ? imageMiniGeneratorFr : imageMiniGeneratorNl}
                  alt={t(($) => $.personal.miniGenerators.imageAlt)}
                />
              </figure>
            </EnergyOverviewSlide>
          )}

          {/* REFERRALS SLIDE */}
          {(referrals?.amount || communityReferrals) && (
            <EnergyOverviewSlide
              color={BrandColors.PINK}
              data={formatAmount(referrals?.amount || communityReferrals, { fractionDigits: 0 })}
              label={t(($) => $[!referrals?.amount ? 'personal' : 'community'].referrals.label)}
              description={t(($) => $[referrals?.amount ? 'personal' : 'community'].referrals.description, {
                amount: referrals?.amount || communityReferrals
              })}
            >
              <img src={referralDefaultImg} className={styles['referrals-image']} alt={t(($) => $.personal.referrals.imageAlt)} />
              <div className={styles['referrals-container']}>
                <Link
                  as="a"
                  representation="button"
                  href={
                    language === LANGUAGES.FR
                      ? `https://www.boltenergie.be/fr/convaincre-un-ami${referrals?.code ? `?generatedReferralCode=${referrals?.code}` : ''}`
                      : `https://www.boltenergie.be/nl/overtuig-een-vriend${
                          referrals?.code ? `?generatedReferralCode=${referrals.code}` : ''
                        }`
                  }
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  {t(($) => $[referrals?.amount ? 'personal' : 'community'].referrals.button)}
                </Link>
              </div>
            </EnergyOverviewSlide>
          )}

          {/* THANKS SLIDE */}
          {!isProducer && <ThanksSlide popConfetti={confetti} name={account.firstName} text={t(($) => $.personal.thanks.text)} />}
        </EnergyOverviewCarousel>
      )}
    </>
  )
}

export default SectionPersonal
